/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react'
import { TABLE_KEYS } from '../Table/models/Filter.model'
import { TableField } from '../Table/components/TableField/TableField'

interface DynamicFormTableFieldProps {
  name: string
  field: any
  methods: any
  mappedDataMatrixValues: Record<string, any>
  fullName: string
}

export function DynamicFormTableField({
  name,
  field,
  methods,
  mappedDataMatrixValues,
  fullName
}: DynamicFormTableFieldProps) {
  const tableRows = useMemo(() => {
    if (!field.dataMatrixFields?.length) return field.defaultValue as any

    const matrixValues = field.dataMatrixFields.map(
      (ref: string) => mappedDataMatrixValues?.[ref] || []
    )

    if (!matrixValues.some((v: unknown) => Array.isArray(v) && v.length > 0))
      return field.defaultValue as any

    const currentRows = methods.getValues(fullName) || []

    const generateCombinations = (
      fieldNames: string[],
      depth = 0,
      prefix: Record<string, any> = {}
    ): Record<string, any>[] => {
      if (depth === fieldNames.length) return [prefix]

      const key = fieldNames[depth]
      const options = mappedDataMatrixValues[key] || []

      if (!options.length) {
        return generateCombinations(fieldNames, depth + 1, prefix)
      }

      return options.flatMap((option: any) =>
        generateCombinations(fieldNames, depth + 1, {
          ...prefix,
          [key]: {
            id: option.value ?? option.id ?? option.label,
            label: option.label
          }
        })
      )
    }

    const combinations = generateCombinations(field.dataMatrixFields)

    return combinations.map(combo => {
      const id = field.dataMatrixFields
        .map((key: string) => combo[key]?.id)
        .filter(Boolean)
        .join('-')

      const existing = currentRows.find((r: any) => r.id === id)

      const labeledData = field.dataMatrixFields.reduce(
        (acc: Record<string, any>, key: string) => ({
          ...acc,
          [key]: combo[key]?.label
        }),
        {}
      )

      return existing || { id, ...labeledData }
    })
  }, [
    field.dataMatrixFields,
    field.defaultValue,
    mappedDataMatrixValues,
    methods,
    fullName
  ])

  const effectiveColumnVisibilityModel = useMemo(() => {
    const base = { ...(field.columnVisibilityModel ?? {}) }

    const rules = field.columnVisibilityRules ?? []
    rules.forEach(
      (rule: {
        dependsOn: string | number
        hideWhenEmpty: any
        columnField: string | number
        hideWhenNotEmpty: any
      }) => {
        const v = mappedDataMatrixValues?.[rule.dependsOn]
        const isEmpty = !v || (Array.isArray(v) && v.length === 0)

        if (rule.hideWhenEmpty) base[rule.columnField] = !isEmpty
        if (rule.hideWhenNotEmpty) base[rule.columnField] = isEmpty
      }
    )

    return base
  }, [
    field.columnVisibilityModel,
    field.columnVisibilityRules,
    mappedDataMatrixValues
  ])

  return (
    <TableField
      key={`${fullName}-${JSON.stringify(mappedDataMatrixValues)}`}
      name={name}
      columns={field.columns || []}
      tableKey={field.tableKey || TABLE_KEYS.DEFAULT_KEY}
      defaultRows={tableRows}
      editable={!field.disabled}
      columnVisibilityModel={effectiveColumnVisibilityModel}
    />
  )
}
